import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { array, number, object, string, boolean, date } from 'yup';
import { isEmpty } from 'lodash';
import 'yup-phone-lite';

import { StepType } from '@components/Base';
import { BMI_MAX, BMI_MIN, CHILD_OCCUPATIONS } from '@config';
import { Genders } from '@models/User';
import { useParseParams } from '@hooks/params';
import { findHealthQuoteParamsSchema } from '@apis/HealthQuoteApi';

import { FormValues, MemberFormValues } from './type';
import { quoteMemberParamsSchema } from '@models/Quote';

export const useFormParams = () => {
  const schema = findHealthQuoteParamsSchema.concat(object({
    me: quoteMemberParamsSchema,
    spouse: quoteMemberParamsSchema.default(undefined),
    sons: array().of(quoteMemberParamsSchema),
    daughters: array().of(quoteMemberParamsSchema),
  }));

  return useParseParams<FormValues>(schema);
}

export const useFormValues = () => {
  const memberValues: MemberFormValues = {
    name: '',
    nameKm: '',
    dob: '',
    age: '',
    gender: Genders.Male,
    height: '',
    weight: '',
    bmi: '',
    noMobileNumber: false,
    mobileNumber: '',
    email: '',
    address: '',
    occupation: '',
    personalCode: '',
    medicalAnswers: [
      {
        answer: '',
        remark: '',
      }
    ]
  };

  const values: FormValues = {
    me: memberValues,
    spouse: { allowUndefined: true, ...memberValues },
    sons: [memberValues],
    daughters: [memberValues],
    isConfirmedDeclaration: false,
  };

  return values;
};


export const useSteps = (questions: { question: string }[]) => {
  const { t } = useTranslation();
  return useMemo<StepType[]>(() => {
    if (!questions) return [];

    const adultSchema = object({
      name: string().required(t('validation:required')),
      nameKm: string().optional(),
      gender: string().required(t('validation:required')),
      dob: date().required(t('validation:required')),
      personalCode: string().optional(),
      occupation: string().required(t('validation:required')),
      email: string().email().optional(),
      noMobileNumber: boolean().optional(),
      mobileNumber: string()
        .required(t('validation:required'))
        .when('noMobileNumber', {
          is: true,
          otherwise: (schema) => schema.phone('KH', t('validation:validPhone', { countryName: t('common:cambodia') })),
        }),
      address: string().required(t('validation:required')),
      weight: number().required(t('validation:required')).positive().integer(),
      height: number().required(t('validation:required')).positive().integer(),
      bmi: number().required(t('validation:required')).positive().min(BMI_MIN).max(BMI_MAX),
    });
    const childSchema = adultSchema.concat(object({
      occupation: string().required(t('validation:required')).oneOf(CHILD_OCCUPATIONS),
    }));
    const medicalAnswerschema = object({
      medicalAnswers: array()
        .of(
          object({
            answer: string(),
            remark: string()
          })
        )
        .compact((value: { answer: string }) => isEmpty(value?.answer))
        .length(questions.length),
    })

    const steps: StepType[] = [
      {
        pathname: 'contact',
        validationSchema: object({
          me: adultSchema,
          spouse: adultSchema.default(undefined),
          sons: array().of(childSchema),
          daughters: array().of(childSchema),
        }),
      },
      {
        pathname: 'question',
        validationSchema: object({
          isConfirmedDeclaration: boolean().oneOf([true]),
          me: medicalAnswerschema,
          spouse: medicalAnswerschema.default(undefined),
          sons: array().of(medicalAnswerschema),
          daughters: array().of(medicalAnswerschema),
        }),
      },
    ];

    return steps;
  }, [questions]);
};
